/**
 * Componente de status e reconexão de webhook do Meta WABA
 * Permite verificar e re-inscrever o app para webhooks
 * 
 * @version 1.0.0
 * @date 2026-01-31
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Radio, 
  RefreshCw, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  Clock,
  Loader2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

// Cria logger com namespace específico para este componente
const logger = createLogger('MetaWabaWebhookStatus');

// ============================================================================
// INTERFACES
// ============================================================================

interface MetaWabaWebhookStatusProps {
  inboxId: string;
  hasCredentials: boolean;
  onStatusChange?: () => void;
}

interface WebhookStatus {
  isSubscribed: boolean | null;
  checkedAt: string | null;
  subscribedApps: Array<{
    whatsapp_business_api_data?: {
      id: string;
      name: string;
    };
  }>;
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function MetaWabaWebhookStatus({ 
  inboxId, 
  hasCredentials,
  onStatusChange 
}: MetaWabaWebhookStatusProps) {
  const { t } = useTranslation('pages');
  
  // Estados locais
  const [status, setStatus] = useState<WebhookStatus>({
    isSubscribed: null,
    checkedAt: null,
    subscribedApps: []
  });
  const [isChecking, setIsChecking] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);

  /**
   * Verifica o status atual da inscrição do webhook
   */
  const handleCheckStatus = async () => {
    if (!hasCredentials) {
      toast.error('Credenciais não configuradas');
      return;
    }

    setIsChecking(true);
    
    try {
      logger.info('[MetaWabaWebhookStatus] Verificando status do webhook...');
      
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        toast.error('Sessão expirada');
        return;
      }

      const response = await supabase.functions.invoke('meta-waba-subscribe-app', {
        body: {
          inbox_id: inboxId,
          action: 'check'
        }
      });

      if (response.error) {
        logger.error('[MetaWabaWebhookStatus] Erro ao verificar status', { error: response.error });
        toast.error('Erro ao verificar status do webhook');
        return;
      }

      const result = response.data;
      
      setStatus({
        isSubscribed: result.is_subscribed,
        checkedAt: result.checked_at,
        subscribedApps: result.subscribed_apps || []
      });

      if (result.is_subscribed) {
        toast.success('Webhook está ativo');
      } else {
        toast.warning('Webhook não está inscrito. Clique em "Reconectar" para corrigir.');
      }

      logger.info('[MetaWabaWebhookStatus] Status verificado', { isSubscribed: result.is_subscribed });
      
    } catch (error) {
      logger.error('[MetaWabaWebhookStatus] Exceção ao verificar status', { error });
      toast.error('Erro ao verificar status');
    } finally {
      setIsChecking(false);
    }
  };

  /**
   * Re-inscreve o app para webhooks
   */
  const handleSubscribe = async () => {
    if (!hasCredentials) {
      toast.error('Credenciais não configuradas');
      return;
    }

    setIsSubscribing(true);
    
    try {
      logger.info('[MetaWabaWebhookStatus] Inscrevendo app para webhooks...');
      
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        toast.error('Sessão expirada');
        return;
      }

      const response = await supabase.functions.invoke('meta-waba-subscribe-app', {
        body: {
          inbox_id: inboxId,
          action: 'subscribe'
        }
      });

      if (response.error) {
        logger.error('[MetaWabaWebhookStatus] Erro ao inscrever', { error: response.error });
        toast.error('Erro ao reconectar webhook');
        return;
      }

      const result = response.data;

      if (result.success) {
        setStatus({
          isSubscribed: true,
          checkedAt: result.subscribed_at,
          subscribedApps: []
        });
        toast.success('Webhook reconectado com sucesso!');
        onStatusChange?.();
      } else {
        toast.error(result.error || 'Falha ao reconectar webhook');
      }

      logger.info('[MetaWabaWebhookStatus] Resultado da inscrição', { success: result.success });
      
    } catch (error) {
      logger.error('[MetaWabaWebhookStatus] Exceção ao inscrever', { error });
      toast.error('Erro ao reconectar webhook');
    } finally {
      setIsSubscribing(false);
    }
  };

  // Renderizar badge de status
  const renderStatusBadge = () => {
    if (status.isSubscribed === null) {
      return (
        <Badge variant="outline" className="gap-1">
          <Clock className="h-3 w-3" />
          Não verificado
        </Badge>
      );
    }

    if (status.isSubscribed) {
      return (
        <Badge className="gap-1 bg-green-500/10 text-green-600 border-green-500/20">
          <CheckCircle2 className="h-3 w-3" />
          Ativo
        </Badge>
      );
    }

    return (
      <Badge variant="destructive" className="gap-1">
        <XCircle className="h-3 w-3" />
        Inativo
      </Badge>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Radio className="h-4 w-4 text-primary" />
              {t('inboxDetails.webhookStatus', 'Status do Webhook')}
            </CardTitle>
            <CardDescription className="text-xs mt-1">
              Recebimento de mensagens via Meta Cloud API
            </CardDescription>
          </div>
          {renderStatusBadge()}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Alerta se webhook não está inscrito */}
        {status.isSubscribed === false && (
          <Alert className="border-destructive/50 bg-destructive/10">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <AlertDescription className="text-destructive text-sm">
              O webhook não está inscrito. Mensagens enviadas pelos contatos não serão recebidas.
              Clique em "Reconectar Webhooks" para corrigir.
            </AlertDescription>
          </Alert>
        )}

        {/* Informações do status */}
        {status.checkedAt && (
          <div className="text-xs text-muted-foreground">
            Última verificação: {format(new Date(status.checkedAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
          </div>
        )}

        {/* Informações sobre modo de coexistência */}
        <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-md">
          <p className="font-medium mb-1">💡 Dica:</p>
          <p>
            Se estiver usando o modo de coexistência (app móvel + Cloud API), 
            algumas mensagens podem não chegar devido a limitações conhecidas da Meta.
          </p>
        </div>

        {/* Botões de ação */}
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleCheckStatus}
            disabled={isChecking || !hasCredentials}
            className="flex-1"
          >
            {isChecking ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Verificar Status
          </Button>

          <Button 
            variant={status.isSubscribed === false ? "default" : "outline"}
            size="sm" 
            onClick={handleSubscribe}
            disabled={isSubscribing || !hasCredentials}
            className={cn(
              "flex-1",
              status.isSubscribed === false && "bg-primary text-primary-foreground"
            )}
          >
            {isSubscribing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Radio className="h-4 w-4 mr-2" />
            )}
            Reconectar Webhooks
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
